import React from 'react';
import { useFees } from '../context/FeeContext';
import { useTranslation } from 'react-i18next';

// Fallback used only until config loads / if none is configured.
const DEFAULT_FEE_ALLOCATION = [
  { label: 'Tax', value: 1.7 },
  { label: 'Payment Processing', value: 2 },
  { label: 'Tech Infrastructure', value: 3.5 },
  { label: 'Customer Support', value: 2.5 },
  { label: 'Operation Cost', value: 2.3 },
  { label: 'Platform Margin', value: 3 },
];

const fmt = (v) => `${parseFloat(Number(v).toFixed(2)).toString()}%`;

/**
 * Shows where the platform service fee / commission is used. The allocation is
 * configurable from the Owner Panel and served via /api/fee-rates (FeeContext).
 *
 * `rate` is the platform fee shown on THIS card (e.g. 7.5 for the client's 7.5%
 * service fee, or the provider's 7.5% commission). The line items are scaled
 * proportionally so they always sum exactly to that rate — so the breakdown
 * matches the fee the user actually pays, regardless of how the owner entered
 * the raw values.
 */
export const FeeBreakdown = ({ className = '', rate }) => {
  const { feeRates } = useFees();
  const { t } = useTranslation();
  const allocation =
    Array.isArray(feeRates?.fee_allocation) && feeRates.fee_allocation.length > 0
      ? feeRates.fee_allocation
      : DEFAULT_FEE_ALLOCATION;

  const rawTotal = allocation.reduce((s, it) => s + (Number(it.value) || 0), 0);
  const targetRate = typeof rate === 'number' && !Number.isNaN(rate) ? rate : rawTotal;
  const scaled = allocation.map((item) => ({
    label: item.label,
    value: rawTotal > 0 ? ((Number(item.value) || 0) / rawTotal) * targetRate : 0,
  }));

  return (
    <div className={`mt-4 pt-4 border-t border-gray-200/70 ${className}`} data-testid="fee-breakdown">
      <p className="text-sm font-semibold text-gray-800 mb-2">{t('payments.whereFeeGoes')}</p>
      <ul className="space-y-1.5">
        {scaled.map((item) => (
          <li
            key={item.label}
            className="flex items-center justify-between text-sm text-gray-700"
            data-testid={`fee-breakdown-${String(item.label).toLowerCase().replace(/\s+/g, '-')}`}
          >
            <span className="flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400" />
              {t(`payments.feeLabels.${item.label}`, item.label)}
            </span>
            <span className="font-semibold text-gray-900 tabular-nums">{fmt(item.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default FeeBreakdown;
